import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { PodzialTekstu } from "../../components/analiza/PodzialTekstu.js";
import { IconChevronLewo, IconStrzalkaDol } from "../../components/icons/index.js";
import { PrzyciskRaportu } from "../../components/ocena/PrzyciskRaportu.js";
import { Rozmowa } from "../../components/transcript/Rozmowa.js";
import { Banner } from "../../components/ui/Banner.js";
import { Button } from "../../components/ui/Button.js";
import { Card } from "../../components/ui/Card.js";
import { Collapse } from "../../components/ui/Collapse.js";
import { ProgressBar } from "../../components/ui/ProgressBar.js";
import { StanBledu, StanLadowania, StanPusty } from "../../components/ui/stany.js";
import { useToast } from "../../components/ui/Toast.js";
import { KartaCwiczenia } from "../../components/uzytkownik/KartaCwiczenia.js";
import { KartaOceny } from "../../components/uzytkownik/KartaOceny.js";
import { KartaWyboruMowcy } from "../../components/uzytkownik/KartaWyboruMowcy.js";
import { KrokiWToku } from "../../components/uzytkownik/KrokiWToku.js";
import { SekcjaWskazan } from "../../components/uzytkownik/SekcjaWskazan.js";
import { WyborMowcy } from "../../components/uzytkownik/WyborMowcy.js";
import { useSesja } from "../../hooks/useSesja.js";
import { api } from "../../lib/api.js";
import { czyMoznaPobracRaport, policzPodswietlenia, zbudujRaport } from "../../lib/raportOceny.js";
import { czekaNaCzlowieka, IN_PROGRESS, postepPipeline, rodzajToru } from "../../lib/sessionStatus.js";
import { uzupelnijBrakiDlaUzytkownika } from "../../lib/widokUzytkownika.js";
import {
  krokiWToku,
  mowcyDoWyboru,
  nicNieOceniono,
  opisRozmowy,
  splaszczWskazania,
  tintDlaIdMowcy,
  tintDlaNazwy,
  uwagiDoOceny,
  ustalMowceUzytkownika,
  wybierzCwiczenie,
  zbudujWidokOceny,
} from "../../lib/wynikUzytkownika.js";

/**
 * Wynik rozmowy konta `user` (trasa /analizy/:id): od ogolu do szczegolu, o NIM.
 *
 * Stany: ladowanie, "Nie ma takiej rozmowy" (404), baner przy chwilowym bledzie sieci (dane zostaja), czekanie na
 * zatwierdzenie podzialu tekstu (PodzialTekstu w wariancie uzytkownika), ocena w toku (trzy proste kroki i pasek), blad analizy,
 * brak mowy w nagraniu i gotowy wynik. Nic tu nie pokazuje kosztow, nazw modeli ani dostawcow, wersji metodyki, tresci
 * bledow z przetwarzania, uwag zadania ani pomiarow brzmienia: serwer i tak ich nie wysyla (uzupelnijBrakiDlaUzytkownika
 * dopelnia tylko puste pola, zeby raport sie nie wywrocil).
 *
 * Wynik dotyczy jednego mowcy, ktory jest uzytkownikiem ("Kim jestes w tej rozmowie"): z sesji (`userSpeakerId`), a gdy
 * jej brak i mowil tylko jeden - ten jeden; w innym razie pytamy na karcie "Który mówca to Ty?". Kazda zmiana mowcy ("Wynik
 * dla:") jest zapisem na serwerze (PUT /api/sessions/:id/ja).
 *
 * Wszystkie liczenia (ocena slowna, najmocniejszy obszar, pierwsze wskazania, wybor cwiczenia) siedza w
 * lib/wynikUzytkownika.ts i maja testy bez przegladarki; tu zostaje sklejanie ekranu.
 */

/** Po tylu ms od otwarcia transkryptu przewijamy do wypowiedzi: tyle trwa animacja wysokosci (M-2), zanim jest dokad skoczyc. */
const OPOZNIENIE_SKOKU_MS = 260;

const ID_TRANSKRYPTU = "wynik-transkrypt";

export function WynikPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  // Przejsciowy blad sieci nie moze zamienic ekranu na "nie ma takiej rozmowy" - odpytywanie i tak ponawia, wiec
  // wystarczy baner stanu (BN-1). `odswiez` pobiera sesje od razu: tak ekran reaguje na wlasne akcje (zatwierdzenie
  // podzialu, wybor mowcy), bo odpytywanie zatrzymuje sie na DONE i na SPLIT_REVIEW.
  const { session: surowaSesja, missing, glitch, odswiez } = useSesja(id);
  const toast = useToast();

  // Odpowiedz dla usera nie ma pol technicznych przebiegow (model, dostawca): dopelniamy je, zanim sesja trafi do raportu.
  const session = useMemo(() => (surowaSesja ? uzupelnijBrakiDlaUzytkownika(surowaSesja) : null), [surowaSesja]);

  const [zapisywanyKlucz, setZapisywanyKlucz] = useState<string | null>(null);
  /**
   * Wybor, ktory serwer przyjal (PUT sie udal), ale odswiezenie sesji jeszcze go nie przyniosl (np. chwilowy brak sieci
   * tuz po zapisie). Bez tego ekran cofalby sie do starego stanu, choc serwer ma juz nowy. Znika, gdy sesja go dogoni.
   */
  const [potwierdzonyKlucz, setPotwierdzonyKlucz] = useState<string | null>(null);
  /** Po pierwszym wyborze karta "Który mówca to Ty?" znika razem z przyciskiem, ktory mial fokus: fokus idzie na tytul. */
  const [fokusNaTytul, setFokusNaTytul] = useState(false);
  const tytulRef = useRef<HTMLHeadingElement>(null);
  /** Zapis wyboru mowcy: trwa/oczekuje. Szybka zmiana w trakcie zapisu nie jest gubiona, tylko zapisuje sie po poprzedniej. */
  const zapisRef = useRef<{ trwa: boolean; oczekuje: string | null }>({ trwa: false, oczekuje: null });
  const [transkryptOtwarty, setTranskryptOtwarty] = useState(false);
  /** Rozmowa montuje sie dopiero po pierwszym otwarciu: nikt, kto jej nie otworzy, nie placi za setki dymkow. */
  const [transkryptWczytany, setTranskryptWczytany] = useState(false);
  const [skokDo, setSkokDo] = useState<{ index: number; token: number } | null>(null);
  const transkryptOtwartyRef = useRef(false);
  transkryptOtwartyRef.current = transkryptOtwarty;
  const timerSkokuRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const poprzedniStatusRef = useRef<string | null>(null);

  useEffect(
    () => () => {
      if (timerSkokuRef.current !== null) clearTimeout(timerSkokuRef.current);
    },
    [],
  );

  // Koniec dlugiego zadania potwierdzamy toastem: znikniecie karty z krokami jest jedynym sygnalem, ze cos sie stalo.
  useEffect(() => {
    const teraz = session?.status ?? null;
    const przed = poprzedniStatusRef.current;
    poprzedniStatusRef.current = teraz;
    if (teraz === "DONE" && przed !== null && IN_PROGRESS.has(przed)) toast.sukces("Wynik jest gotowy.");
  }, [session?.status, toast]);

  const raport = useMemo(() => (session ? zbudujRaport(session) : null), [session]);
  const podswietlenia = useMemo(
    () => (session ? policzPodswietlenia(session.utterances, session.lensRuns) : null),
    [session],
  );
  const tinty = useMemo(
    () => (session ? { dlaNazwy: tintDlaNazwy(session), dlaId: tintDlaIdMowcy(session) } : null),
    [session],
  );

  const ustalenie = useMemo(
    () => (session && raport ? ustalMowceUzytkownika(session.userSpeakerId ?? null, raport.mowcy) : null),
    [session, raport],
  );
  const kandydaci = useMemo(() => (raport ? mowcyDoWyboru(raport.mowcy) : []), [raport]);
  // Podczas zapisu pokazujemy od razu wybranego mowce (dane sa juz u nas), a po nieudanym zapisie wracamy do poprzedniego.
  const aktualnyKlucz =
    zapisywanyKlucz ??
    potwierdzonyKlucz ??
    (ustalenie && (ustalenie.rodzaj === "wybrany" || ustalenie.rodzaj === "jedyny") ? ustalenie.klucz : null);

  // Sesja dogonila zapis: lokalne potwierdzenie przestaje byc potrzebne.
  const zapisanyWSesji = session?.userSpeakerId ?? null;
  useEffect(() => {
    if (potwierdzonyKlucz !== null && zapisanyWSesji === potwierdzonyKlucz) setPotwierdzonyKlucz(null);
  }, [potwierdzonyKlucz, zapisanyWSesji]);
  const mowca = useMemo(() => raport?.mowcy.find((m) => m.klucz === aktualnyKlucz) ?? null, [raport, aktualnyKlucz]);

  const widok = useMemo(() => (mowca && raport ? zbudujWidokOceny(mowca, raport.parametry) : null), [mowca, raport]);
  const mocneStrony = useMemo(() => (mowca ? splaszczWskazania(mowca.mocneStrony) : []), [mowca]);
  const doPoprawy = useMemo(() => (mowca ? splaszczWskazania(mowca.doPoprawy) : []), [mowca]);
  const cwiczenie = useMemo(
    () => (mowca && widok ? wybierzCwiczenie(mowca, widok.najwiecejDoPoprawy) : null),
    [mowca, widok],
  );

  const pierwszyWybor = ustalenie?.rodzaj === "do-wyboru";
  const zapiszMowce = useCallback(
    async (klucz: string): Promise<void> => {
      if (!id) return;
      const zapis = zapisRef.current;
      if (pierwszyWybor) setFokusNaTytul(true);
      // Ekran od razu pokazuje ostatni wybor. Gdy poprzedni zapis jeszcze trwa, ten idzie zaraz po nim (tylko najnowszy).
      setZapisywanyKlucz(klucz);
      if (zapis.trwa) {
        zapis.oczekuje = klucz;
        return;
      }
      zapis.trwa = true;
      let doZapisu: string | null = klucz;
      let ostatniZapisany: string | null = null;
      try {
        while (doZapisu !== null) {
          await api.ustawMowceUzytkownika(id, doZapisu);
          ostatniZapisany = doZapisu;
          doZapisu = zapis.oczekuje;
          zapis.oczekuje = null;
        }
        setPotwierdzonyKlucz(ostatniZapisany);
        await odswiez();
      } catch {
        // Zapis przyjety wczesniej (ostatniZapisany) zostaje; nieudany nie: ekran wraca do tego, co ma serwer.
        zapis.oczekuje = null;
        setFokusNaTytul(false);
        if (ostatniZapisany !== null) setPotwierdzonyKlucz(ostatniZapisany);
        toast.blad("Nie udało się zapisać wyboru. Spróbuj jeszcze raz.");
      } finally {
        zapis.trwa = false;
        setZapisywanyKlucz(null);
      }
    },
    [id, odswiez, pierwszyWybor, toast],
  );

  // Po wyborze mowcy z karty fokus wraca na tytul, gdy tylko ekran wyniku sie pojawi (a nie na <body>).
  useEffect(() => {
    if (!fokusNaTytul || widok === null) return;
    tytulRef.current?.focus({ preventScroll: true });
    setFokusNaTytul(false);
  }, [fokusNaTytul, widok]);

  /** Numer wypowiedzi z karty wskazania: rozwija rozmowe i przewija do tej wypowiedzi. */
  const pokazWypowiedz = useCallback((indeks: number) => {
    const bylOtwarty = transkryptOtwartyRef.current;
    setTranskryptWczytany(true);
    setTranskryptOtwarty(true);
    const przewin = (): void => setSkokDo((poprzedni) => ({ index: indeks, token: (poprzedni?.token ?? 0) + 1 }));
    if (timerSkokuRef.current !== null) clearTimeout(timerSkokuRef.current);
    // Zwiniete: najpierw niech sie rozwinie, inaczej nie ma dokad przewinac (a fokus nie wejdzie w ukryta tresc).
    if (bylOtwarty) przewin();
    else timerSkokuRef.current = setTimeout(przewin, OPOZNIENIE_SKOKU_MS);
  }, []);

  const przelaczTranskrypt = useCallback(() => {
    setTranskryptWczytany(true);
    setTranskryptOtwarty((o) => !o);
  }, []);

  // ---------------------------------------------------------------------------------------------------------------
  // Stany bez danych
  // ---------------------------------------------------------------------------------------------------------------

  if (missing) {
    return (
      <>
        <h1 className="page-title">Nie ma takiej rozmowy</h1>
        <p className="page-lead">Adres jest nieaktualny albo rozmowa została usunięta.</p>
        <Card>
          <StanPusty
            tytul="Ta rozmowa nie istnieje"
            opis="Sprawdź adres albo wróć do listy rozmów."
            akcja={
              <Link className="btn btn--secondary" to="/">
                Moje rozmowy
              </Link>
            }
          />
        </Card>
      </>
    );
  }

  if (!session || !raport || !podswietlenia || !tinty) {
    return (
      <>
        <h1 className="page-title">Rozmowa</h1>
        <p className="page-lead">Wczytuję rozmowę.</p>
        {/* Wariant info, nie warning: w widoku oceny bursztyn znaczy "najlepsze" (ten sam baner co nizej, tez pilny). */}
        {glitch && <Banner wariant="info" pilny tekst="Chwilowy problem z połączeniem - ponawiam próbę." />}
        <Card>
          <StanLadowania tekst="Wczytuję rozmowę..." />
        </Card>
      </>
    );
  }

  // ---------------------------------------------------------------------------------------------------------------
  // Dane sa: jeden z szesciu stanow
  // ---------------------------------------------------------------------------------------------------------------

  const status = session.status;
  const gotowe = status === "DONE";
  const nieudane = status === "FAILED";
  // DONE, a zadnego parametru nie udalo sie ocenic (wszystkie przebiegi nieudane albo zadanie oceny padlo): to porazka
  // oceny, nie rozmowa bez uwag. Ekran mowi to wprost i nie pokazuje "nie znalezlismy nic do poprawy".
  const ocenaNieudana = gotowe && raport.stan === "raport" && nicNieOceniono(raport.parametry);
  const brakMowy = status === "NO_SPEECH";
  const czekaNaZatwierdzenie = czekaNaCzlowieka(status);
  const wToku = !gotowe && !nieudane && !brakMowy && !czekaNaZatwierdzenie;
  const maWypowiedzi = session.utterances.length > 0;
  const jestTekst = session.inputKind === "TEXT";

  const opisGotowej = ocenaNieudana
    ? "Nie udało się ocenić tej rozmowy."
    : opisRozmowy(session.inputKind, session.utterances.length, mowca?.nazwa ?? null);
  const lead = gotowe
    ? opisGotowej
    : czekaNaZatwierdzenie
      ? "Został jeden krok: zatwierdź podział tekstu."
      : nieudane
        ? "Nie udało się ocenić tej rozmowy."
        : brakMowy
          ? "W tym nagraniu nie było czego oceniać."
          : "Oceniamy Twoją rozmowę. To zwykle zajmuje kilka minut.";

  const naglowek = (
    <>
      <Link className="wynik-powrot" to="/">
        <IconChevronLewo size={16} />
        Moje rozmowy
      </Link>
      {/* tabIndex -1: programowy fokus po wyborze mowcy z karty (karta znika razem z przyciskiem, ktory go mial). */}
      <h1 className="page-title" ref={tytulRef} tabIndex={-1}>
        {session.title}
      </h1>
      <p className="page-lead">{lead}</p>
      {/* BN-1: baner stanu, nie zdarzenie. Dane zostaja na ekranie, odpytywanie ponawia samo. Wariant info (ikona i tekst,
          ST-3), nie warning: w widoku oceny bursztyn (--status-warning) znaczy "najlepsze" (decyzja z 29.10.2026). Pilny,
          bo wczesniej byl ostrzezeniem: zmiana koloru nie zmienia tego, ze czytnik oglasza go od razu. */}
      {glitch && <Banner wariant="info" pilny tekst="Chwilowy problem z połączeniem - ponawiam próbę." />}
    </>
  );

  if (czekaNaZatwierdzenie) {
    return (
      <>
        {naglowek}
        {session.context && (
          <Card tytul="Opis rozmowy">
            <p className="opis-rozmowy">{session.context}</p>
            <p className="field-hint">
              Mówców przypisaliśmy na podstawie tego opisu. Sprawdź podział poniżej i zatwierdź go.
            </p>
          </Card>
        )}
        {/* Wariant uzytkownika: bez wyboru parametrow, bez wzmianki o kosztach i modelu; zatwierdzenie jedzie ze wszystkimi
            wlaczonymi parametrami (components/analiza/PodzialTekstu.tsx). */}
        <PodzialTekstu sesja={session} odswiez={odswiez} tintDlaId={tinty.dlaId} wariant="uzytkownik" />
      </>
    );
  }

  if (wToku) {
    const tor = rodzajToru({
      inputKind: session.inputKind,
      status,
      speakersAssignedBy: session.speakersAssignedBy,
      maWypowiedzi,
    });
    const postep = postepPipeline(status, tor);
    const kroki = krokiWToku(status);
    return (
      <>
        {naglowek}
        <Card>
          <KrokiWToku kroki={kroki} />
          <ProgressBar etykieta="Postęp oceny" procent={(postep.zrobione / postep.wszystkie) * 100} />
          <p className="wynik-uwaga">Możesz tu zostać albo wrócić do listy: wynik pojawi się na niej, gdy będzie gotowy.</p>
          <div className="wynik-akcje">
            <Link className="btn btn--secondary" to="/">
              Wróć do listy
            </Link>
          </div>
        </Card>
      </>
    );
  }

  if (nieudane) {
    return (
      <>
        {naglowek}
        <Card>
          <StanBledu
            tytul="Analiza nie powiodła się."
            // Serwer podmienia KAZDY tekst bledu dla tego konta na jedno ogolne zdanie, wiec session.error nie niesie
            // wskazowki. Zawsze stala podpowiedz, zalezna tylko od tego, czy to tekst, ktory nie doszedl do podzialu.
            opis={
              jestTekst && !maWypowiedzi
                ? "Nie udało się podzielić tekstu na wypowiedzi. Wgraj go jeszcze raz, najlepiej z dokładniejszym opisem rozmowy."
                : "Spróbuj wgrać rozmowę jeszcze raz. Jeśli to się powtórzy, daj nam znać."
            }
            akcja={
              <Link className="btn btn--secondary" to="/nowa">
                Oceń rozmowę jeszcze raz
              </Link>
            }
          />
        </Card>
      </>
    );
  }

  if (brakMowy) {
    return (
      <>
        {naglowek}
        <Card>
          <StanPusty
            tytul="Nie wykryliśmy w nagraniu mowy."
            opis="To nie jest błąd po naszej stronie. Sprawdź, czy plik zawiera rozmowę, i wgraj go jeszcze raz."
            akcja={
              <Link className="btn btn--secondary" to="/nowa">
                Oceń rozmowę
              </Link>
            }
          />
        </Card>
      </>
    );
  }

  // Status DONE, ale nie ma z czego zlozyc wyniku: stare analizy bez oceny albo bez wypowiedzi.
  if (raport.stan !== "raport") {
    return (
      <>
        {naglowek}
        <Card>
          <StanPusty
            tytul={raport.stan === "brak-wypowiedzi" ? "Nie ma czego oceniać" : "Ta rozmowa nie ma jeszcze oceny"}
            opis={
              raport.stan === "brak-wypowiedzi"
                ? "W tej rozmowie nie rozpoznaliśmy żadnych wypowiedzi."
                : "Wgraj ją jeszcze raz, a ocenimy ją od razu."
            }
            akcja={
              <Link className="btn btn--secondary" to="/nowa">
                Oceń rozmowę
              </Link>
            }
          />
        </Card>
      </>
    );
  }

  if (ocenaNieudana) {
    return (
      <>
        {naglowek}
        <Card>
          <StanBledu
            tytul="Ocena nie powiodła się."
            opis="Spróbuj wgrać rozmowę jeszcze raz. Jeśli to się powtórzy, daj nam znać."
            akcja={
              <Link className="btn btn--secondary" to="/nowa">
                Oceń rozmowę jeszcze raz
              </Link>
            }
          />
        </Card>
      </>
    );
  }

  // Raport da sie pobrac, gdy jest co w nim pokazac: liczymy tylko przebiegi, ktore sie udaly (nieudane nie niosa wynikow).
  const liczbaPrzebiegow = raport.parametry.filter((p) => p.przebieg !== null && p.przebieg.status === "OK").length;
  const akcje = (
    <>
      <div className="wynik-akcje">
        <PrzyciskRaportu
          sesjaId={session.id}
          mozliwosc={czyMoznaPobracRaport(status, liczbaPrzebiegow)}
          wariant="uzytkownik"
        />
        <Link className="btn btn--primary" to="/nowa">
          Oceń kolejną rozmowę
        </Link>
      </div>
      {/* Raport Word i PDF obejmuje wszystkich mowcow, a ekran jest "o Tobie": bez tego zdania latwo wyslac dalej ocene drugiej osoby. */}
      {kandydaci.length > 1 && <p className="wynik-uwaga">Raport obejmuje wszystkich mówców z tej rozmowy.</p>}
    </>
  );

  // Nie wiadomo, kim jest uzytkownik (kilku mowcow, brak wyboru): bez tego nie ma o kim pokazac wyniku.
  if (!mowca || !widok) {
    return (
      <>
        {naglowek}
        {ustalenie?.rodzaj === "do-wyboru" ? (
          <KartaWyboruMowcy kandydaci={kandydaci} zapisuje={zapisywanyKlucz !== null} onWybierz={(k) => void zapiszMowce(k)} />
        ) : (
          <Card>
            <StanPusty tytul="Nie ma czego oceniać" opis="W tej rozmowie nie rozpoznaliśmy żadnych wypowiedzi." />
          </Card>
        )}
        {akcje}
      </>
    );
  }

  const przelacznik =
    kandydaci.length > 1 ? (
      <div className="wynik-przelacznik">
        <WyborMowcy
          etykieta="Wynik dla:"
          opcje={kandydaci.map((m) => ({ wartosc: m.klucz, etykieta: m.nazwa }))}
          wartosc={mowca.klucz}
          onZmiana={(k) => void zapiszMowce(k)}
        />
      </div>
    ) : undefined;

  /** Legenda kolorow ma sens, gdy jakis parametr sie policzyl: tylko wtedy kolory moga z czegos wynikac. */
  const pokazLegende = raport.parametry.some((p) => p.stan.rodzaj === "oceniono" || p.stan.rodzaj === "brak-wskazan");

  return (
    <>
      {naglowek}

      <KartaOceny widok={widok} uwagi={uwagiDoOceny(widok, raport)} przelacznik={przelacznik} />

      <SekcjaWskazan
        rodzaj="mocna-strona"
        elementy={mocneStrony}
        onPokaz={pokazWypowiedz}
        niepelna={widok.niepelna}
        key={`mocne-${mowca.klucz}`}
      />
      <SekcjaWskazan
        rodzaj="do-poprawy"
        elementy={doPoprawy}
        onPokaz={pokazWypowiedz}
        niepelna={widok.niepelna}
        key={`poprawy-${mowca.klucz}`}
      />

      {cwiczenie && <KartaCwiczenia cwiczenie={cwiczenie} zrodloAnalizyId={session.id} />}

      <section aria-labelledby="wynik-rozmowa-naglowek">
        <h2 className="wynik-sekcja" id="wynik-rozmowa-naglowek">
          Cała rozmowa
        </h2>
        <Card>
          <Button
            wariant="secondary"
            aria-expanded={transkryptOtwarty}
            aria-controls={ID_TRANSKRYPTU}
            onClick={przelaczTranskrypt}
          >
            <IconStrzalkaDol size={16} />
            {transkryptOtwarty ? "Ukryj transkrypt" : "Pokaż transkrypt z kolorami"}
          </Button>
          <Collapse otwarty={transkryptOtwarty} id={ID_TRANSKRYPTU}>
            <div className="wynik-transkrypt">
              {transkryptWczytany && (
                <Rozmowa
                  utterances={session.utterances}
                  tintDlaMowcy={tinty.dlaNazwy}
                  modelEkspresji={null}
                  zakresy={podswietlenia.zakresy}
                  biezacyIndex={null}
                  naPrzewin={null}
                  dlugoscSek={session.durationSec}
                  pokazCzas={session.hasTimestamps}
                  rodzajMaterialu={jestTekst ? "tekst" : "nagranie"}
                  jakosci={podswietlenia.jakosci}
                  pokazLegende={pokazLegende}
                  skokDo={skokDo}
                />
              )}
            </div>
          </Collapse>
        </Card>
      </section>

      {akcje}
    </>
  );
}
